import React, { useState, useEffect } from 'react';
import { useMediaQuery, useTheme } from '@material-ui/core';
import FilterModal from '../FilterModal';
import { useGetCurrentUserQuery } from 'utils/wemble-api';
import { TeamFacetCount } from 'utils/wemble-api.generated';

interface TeamModalProps {
  visible: boolean;
  onClose: () => void;
  onSave: (teamIds: string[]) => void;
  teams: TeamFacetCount[];
  initialSelected: string[];
  showAllOption?: boolean;
}

const TeamModal = ({ visible, onClose, onSave, teams, initialSelected, showAllOption = true }: TeamModalProps) => {
  const { data: currentUser } = useGetCurrentUserQuery();
  const userGroupIds = [
    typeof currentUser?.group === 'string' ? currentUser.group : currentUser?.group?._id,
    typeof currentUser?.secondaryGroup === 'string' ? currentUser.secondaryGroup : (currentUser?.secondaryGroup as any)?._id,
  ].filter(Boolean) as string[];
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selected, setSelected] = useState<string[]>(initialSelected);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (visible) {
      setSelected(initialSelected);
      setSearch('');
    }
  }, [visible]);

  // Group teams by group, sort: user's groups first, then selected, then rest
  // Recomputed when initialSelected changes (i.e., after save + reopen)
  const sortOrderRef = React.useRef<string[]>([]);
  useEffect(() => {
    const allGroupIds = Array.from(new Set(teams.map(t => t.groupId)));
    const groupCounts = new Map<string, number>();
    for (const t of teams) groupCounts.set(t.groupId, (groupCounts.get(t.groupId) || 0) + t.count);
    sortOrderRef.current = [...allGroupIds].sort((a, b) => {
      const aIsUser = userGroupIds.includes(a) ? -1 : 0;
      const bIsUser = userGroupIds.includes(b) ? -1 : 0;
      if (aIsUser !== bIsUser) return aIsUser - bIsUser;
      const aSelected = initialSelected[0] === '__all__' || teams.filter(t => t.groupId === a).some(t => initialSelected.includes(t._id)) ? -1 : 0;
      const bSelected = initialSelected[0] === '__all__' || teams.filter(t => t.groupId === b).some(t => initialSelected.includes(t._id)) ? -1 : 0;
      if (aSelected !== bSelected) return aSelected - bSelected;
      return (groupCounts.get(b) || 0) - (groupCounts.get(a) || 0);
    });
  }, [initialSelected, teams]);

  const grouped: { groupId: string; groupName: string; teams: TeamFacetCount[] }[] = (() => {
    const map = new Map<string, { groupId: string; groupName: string; teams: TeamFacetCount[] }>();
    teams.forEach(t => {
      if (!map.has(t.groupId)) {
        map.set(t.groupId, { groupId: t.groupId, groupName: t.groupName, teams: [] });
      }
      map.get(t.groupId)!.teams.push(t);
    });
    const groups = Array.from(map.values());
    // Sort by locked order
    if (sortOrderRef.current.length) {
      groups.sort((a, b) => {
        const ai = sortOrderRef.current.indexOf(a.groupId);
        const bi = sortOrderRef.current.indexOf(b.groupId);
        return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
      });
    }
    return groups;
  })();

  const isTeamSelected = (teamId: string) => isAll || selected.includes(teamId);

  const filteredGrouped = search
    ? grouped.map(g => ({
        ...g,
        teams: g.teams.filter(t =>
          isTeamSelected(t._id) ||
          t.name.toLowerCase().includes(search.toLowerCase()) ||
          t.groupName.toLowerCase().includes(search.toLowerCase())
        ),
      })).filter(g => g.teams.length > 0)
    : grouped;

  const isAll = selected.length === 1 && selected[0] === '__all__';

  const toggleTeam = (teamId: string) => {
    if (isAll) {
      // Switch from "all" to everything except this one
      const allIds = teams.map(t => t._id);
      setSelected(allIds.filter(id => id !== teamId));
      return;
    }
    if (selected.includes(teamId)) {
      const next = selected.filter(id => id !== teamId);
      setSelected(next);
    } else {
      const next = [...selected, teamId];
      // If all teams now selected, switch to __all__
      if (teams.every(t => next.includes(t._id))) {
        setSelected(['__all__']);
      } else {
        setSelected(next);
      }
    }
  };

  const toggleGroup = (groupId: string) => {
    const groupTeamIds = teams.filter(t => t.groupId === groupId).map(t => t._id);
    if (isAll) {
      // Deselect this group from all
      const allIds = teams.map(t => t._id);
      setSelected(allIds.filter(id => !groupTeamIds.includes(id)));
      return;
    }
    const allGroupSelected = groupTeamIds.every(id => selected.includes(id));
    let next: string[];
    if (allGroupSelected) {
      next = selected.filter(id => !groupTeamIds.includes(id));
    } else {
      const existing = new Set(selected);
      groupTeamIds.forEach(id => existing.add(id));
      next = Array.from(existing);
    }
    if (teams.every(t => next.includes(t._id))) {
      setSelected(['__all__']);
    } else {
      setSelected(next);
    }
  };

  const handleAllClick = () => {
    if (isAll) {
      setSelected([]);
    } else {
      setSelected(['__all__']);
    }
  };

  const totalCount = teams.reduce((sum, t) => sum + t.count, 0);

  const isGroupFullySelected = (groupId: string) => {
    if (isAll) return true;
    return teams.filter(t => t.groupId === groupId).every(t => selected.includes(t._id));
  };
  const isGroupPartiallySelected = (groupId: string) => {
    if (isAll) return false;
    const groupTeams = teams.filter(t => t.groupId === groupId);
    const selectedCount = groupTeams.filter(t => selected.includes(t._id)).length;
    return selectedCount > 0 && selectedCount < groupTeams.length;
  };

  const handleSave = () => {
    onSave(selected);
    onClose();
  };

  const Checkbox = ({ checked, partial }: { checked: boolean; partial?: boolean }) => (
    <div style={{
      width: 18, height: 18, borderRadius: 4,
      border: checked || partial ? 'none' : '2px solid #ccc',
      backgroundColor: checked ? '#4A9BBF' : partial ? '#9DD0E8' : 'white',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 12, color: 'white', fontWeight: 700, flexShrink: 0,
    }}>
      {checked && '✓'}
      {partial && '—'}
    </div>
  );

  const Row = ({ onClick, selected, partial, label, count, indent, bold }: {
    onClick: () => void; selected: boolean; partial?: boolean; label: string; count: number; indent?: boolean; bold?: boolean;
  }) => (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: `${indent ? 4 : 6}px 12px ${indent ? 4 : 6}px ${indent ? 32 : 12}px`,
        borderRadius: 6, cursor: 'pointer',
        backgroundColor: selected && !partial ? '#F0F7FA' : 'transparent',
        transition: 'background-color 0.1s',
        userSelect: 'none', WebkitUserSelect: 'none',
      }}
      onMouseEnter={e => { if (!selected) e.currentTarget.style.backgroundColor = '#FAFAFA'; }}
      onMouseLeave={e => { if (!selected) e.currentTarget.style.backgroundColor = 'transparent'; }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Checkbox checked={selected && !partial} partial={partial} />
        <span style={{ fontSize: indent ? 13 : 14, color: '#333', fontWeight: bold ? 600 : 400 }}>{label}</span>
      </div>
      {count > 0 && <span style={{ fontSize: 12, color: '#999' }}>{count}</span>}
    </div>
  );

  return (
    <FilterModal
      visible={visible}
      onClose={onClose}
      onSave={handleSave}
      title="Practice Groups"
      description="Choose which practice groups to include"
      requireSelection
      selectionCount={selected.length}
    >
      <div>
        {teams.length > 5 && (
          <input
            autoFocus={!isMobile}
            type="text"
            placeholder="Search groups..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%', padding: '8px 12px', borderRadius: 8,
              border: '1px solid #e0e0e0', fontSize: 13, marginBottom: 8,
              outline: 'none', boxSizing: 'border-box',
            }}
          />
        )}

        {showAllOption && (
          <Row
            onClick={handleAllClick}
            selected={isAll}
            label="All groups"
            count={0}
            bold
          />
        )}

        <div style={{ borderTop: showAllOption ? '1px solid #f0f0f0' : 'none', marginTop: showAllOption ? 4 : 0, paddingTop: showAllOption ? 4 : 0 }}>
          {filteredGrouped.map((group, i) => {
            const groupCount = group.teams.reduce((sum, t) => sum + t.count, 0);
            const singleOffice = group.teams.length === 1;

            return (
              <div key={group.groupId} style={{ marginTop: i > 0 ? 2 : 0 }}>
                <Row
                  onClick={() => toggleGroup(group.groupId)}
                  selected={isGroupFullySelected(group.groupId)}
                  partial={isGroupPartiallySelected(group.groupId)}
                  label={singleOffice ? `${group.groupName}` : group.groupName}
                  count={groupCount}
                  bold
                />
                {!singleOffice && group.teams.map(team => (
                  <Row
                    key={team._id}
                    onClick={() => toggleTeam(team._id)}
                    selected={isTeamSelected(team._id)}
                    label={team.name}
                    count={team.count}
                    indent
                  />
                ))}
              </div>
            );
          })}
        </div>

        {filteredGrouped.length === 0 && (
          <div style={{ padding: 20, textAlign: 'center', color: '#999', fontSize: 13 }}>
            {search ? 'No groups match your search' : 'No groups available'}
          </div>
        )}
      </div>
    </FilterModal>
  );
};

export default TeamModal;
